// shared/API.md §47 — «Reportar mensaje», «Bloquear» / «Desbloquear», Configuración › Usuarios bloqueados.
//
// ModerationHost is mounted once by the workspace; a row's menu and the profile card ask for its
// dialogs through the tiny store below (the profile card closes on any click outside it, so the
// confirmation cannot live inside it).
import { useContext, useState, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import { userDisplayName } from '../../../core/channelListDerivation';
import { canReport, REASONS, reasonLabelKey, type ReportReason } from '../../../core/moderation';
import { PostState, type PostRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { Button, Dialog } from '../../kit/Kit';
import { MESSAGE_ENTRY_ATTRIBUTES } from '../../conversation/messageEntry';
import { ShellContext } from '../../shell/ShellContext';
import './moderation.css';

type Request = { kind: 'report'; post: PostRecord; authorName: string } | { kind: 'block'; userId: string; name: string };

let current: Request | null = null;
const listeners = new Set<() => void>();
function setRequest(next: Request | null): void {
  current = next;
  for (const l of listeners) l();
}
function useRequest(): Request | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}

export function openReport(post: PostRecord, authorName: string): void {
  setRequest({ kind: 'report', post, authorName });
}

export function openBlockConfirm(userId: string, name: string): void {
  setRequest({ kind: 'block', userId, name });
}

/** The row menu's «Reportar mensaje» — only where §47 allows it. */
export function ReportMenuItem({ post, authorName, onDone }: { post: PostRecord; authorName: string; onDone(): void }) {
  const shell = useContext(ShellContext);
  if (!shell || !canReport({ userId: post.userId, type: post.type, synced: post.state === PostState.Synced }, shell.me.id)) return null;
  return (
    <button
      className="menu-item danger"
      role="menuitem"
      data-menu="report"
      onClick={() => {
        onDone();
        openReport(post, authorName);
      }}
    >
      <Icon name="alertCircle" size={16} /> {t('moderation.report.action')}
    </button>
  );
}

export function ModerationHost() {
  const request = useRequest();
  const shell = useContext(ShellContext);
  if (!shell || !request) return null;
  const close = () => setRequest(null);
  return request.kind === 'report' ? (
    <ReportDialog key={request.post.id} post={request.post} authorName={request.authorName} onClose={close} onResult={shell.toast} />
  ) : (
    <Dialog
      title={tf('moderation.block.confirmTitle', request.name)}
      onClose={close}
      width={420}
      footer={
        <>
          <Button kind="quiet" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            kind="danger"
            onClick={() => {
              close();
              void data.call('setBlocked', request.userId, true).then((r) => {
                if (!r.ok) shell.toast(r.message || t('moderation.block.failed'), 'error');
              });
            }}
          >
            {t('moderation.block.action')}
          </Button>
        </>
      }
    >
      <p className="moderation-body">{t('moderation.block.confirmBody')}</p>
    </Dialog>
  );
}

function ReportDialog({ post, authorName, onClose, onResult }: { post: PostRecord; authorName: string; onClose(): void; onResult(text: string, tone?: 'info' | 'error'): void }) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [comment, setComment] = useState('');
  const send = () => {
    if (!reason) return;
    onClose();
    void data.call('reportPost', post.id, reason, comment).then(
      (r) => (r.ok ? onResult(t('moderation.report.sent')) : onResult(r.message || t('moderation.report.failed'), 'error')),
      () => onResult(t('moderation.report.failed'), 'error'),
    );
  };
  const snippet = post.message.trim().slice(0, 280);
  return (
    <Dialog
      title={t('moderation.report.title')}
      onClose={onClose}
      width={480}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="danger" disabled={!reason} onClick={send}>
            {t('moderation.report.send')}
          </Button>
        </>
      }
    >
      {snippet ? (
        <div className="moderation-quote">
          <div className="who">{authorName}</div>
          <div className="what">{snippet}</div>
        </div>
      ) : null}
      <p className="moderation-body">{t('moderation.report.prompt')}</p>
      <fieldset className="moderation-reasons" role="radiogroup" aria-label={t('moderation.report.title')}>
        {REASONS.map((r) => (
          <label key={r} className={`moderation-reason${reason === r ? ' selected' : ''}`}>
            <input type="radio" name="moderation-reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
            <span>{t(reasonLabelKey(r))}</span>
          </label>
        ))}
      </fieldset>
      <textarea
        className="moderation-comment"
        {...MESSAGE_ENTRY_ATTRIBUTES}
        rows={2}
        maxLength={500}
        value={comment}
        placeholder={t('moderation.report.comment')}
        aria-label={t('moderation.report.comment')}
        onChange={(e) => setComment(e.target.value)}
      />
    </Dialog>
  );
}

/** The profile card's «Bloquear» / «Desbloquear» (never on the reader's own card). */
export function BlockButton({ userId, name, onBeforeConfirm }: { userId: string; name: string; onBeforeConfirm(): void }) {
  const shell = useContext(ShellContext);
  const { value } = useLive(() => data.call('blockedUsers'), ['users'], []);
  if (!shell || userId === shell.me.id) return null;
  const blocked = !!value?.ids.includes(userId);
  return (
    <button
      type="button"
      className="btn btn-secondary profile-card-block"
      data-blocked={blocked ? 'true' : 'false'}
      onClick={() => {
        if (blocked) {
          void data.call('setBlocked', userId, false).then((r) => {
            if (!r.ok) shell.toast(r.message || t('moderation.unblock.failed'), 'error');
          });
          return;
        }
        onBeforeConfirm();
        openBlockConfirm(userId, name);
      }}
    >
      <Icon name={blocked ? 'person' : 'bellSlash'} size={15} />
      <span>{blocked ? t('moderation.unblock.action') : t('moderation.block.action')}</span>
    </button>
  );
}

/** Configuración › Usuarios bloqueados. */
export function BlockedUsersSection() {
  const shell = useContext(ShellContext);
  const { value } = useLive(() => data.call('blockedUsers'), ['users'], []);
  const users = new Map((value?.users ?? []).map((u) => [u.id, u]));
  const ids = value?.ids ?? [];
  return (
    <section className="moderation-blocked" aria-label={t('moderation.blocked.title')}>
      <p className="moderation-body">{t('moderation.blocked.footer')}</p>
      {value && ids.length === 0 ? <div className="moderation-empty">{t('moderation.blocked.empty')}</div> : null}
      <ul className="moderation-blocked-list">
        {ids.map((id) => {
          const u = users.get(id);
          const name = u ? userDisplayName(u) : '…';
          return (
            <li key={id} className="moderation-blocked-row">
              <Avatar userId={id} name={name} size={32} version={u?.lastPictureUpdate} />
              <span className="names">
                <span className="name">{name}</span>
                {u ? <span className="handle">@{u.username}</span> : null}
              </span>
              <Button
                kind="secondary"
                onClick={() =>
                  void data.call('setBlocked', id, false).then((r) => {
                    if (!r.ok) shell?.toast(r.message || t('moderation.unblock.failed'), 'error');
                  })
                }
              >
                {t('moderation.unblock.action')}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
